import React, { useState, useEffect } from 'react';
import { Download, Upload, Users, MapPin, Eye, Compass, ChevronDown, ChevronUp, History, FileJson, Clock, TrendingUp, TrendingDown } from 'lucide-react';

const RKDouanes = () => {
  const [selectedVille, setSelectedVille] = useState('');
  const [listeVillageoiseJour, setListeVillageoiseJour] = useState('');
  const [listeVillageoiseVeille, setListeVillageoiseVeille] = useState('');
  const [listeGroupes, setListeGroupes] = useState('');
  const [visions, setVisions] = useState('');
  const [evenements, setEvenements] = useState('');
  const [rapportPrevot, setRapportPrevot] = useState('');
  const [listeVilains, setListeVilains] = useState('');
  const [vilainsRecherches, setVilainsRecherches] = useState('');
  const [analyseIntercesseur, setAnalyseIntercesseur] = useState('');
  const [resultats, setResultats] = useState(null);
  const [vilainsDetectes, setVilainsDetectes] = useState([]);

  // États pour les nouvelles fonctionnalités
  const [villesRecentes, setVillesRecentes] = useState([]);
  const [historiqueRapports, setHistoriqueRapports] = useState([]);
  const [previewMouvements, setPreviewMouvements] = useState(null);
  const [autoSaveEnabled, setAutoSaveEnabled] = useState(true);

  // États pour gérer l'ouverture des sections (accordéons)
  const [sectionsOuvertes, setSectionsOuvertes] = useState({
    ville: true,
    villageois: true,
    groupes: true,
    visions: true,
    prevot: true,
    intercesseurs: true,
    resultats: true
  });

  // Charger les préférences depuis les cookies au démarrage
  useEffect(() => {
    const cookiesSections = document.cookie
      .split('; ')
      .find(row => row.startsWith('rk_sections='));
    
    if (cookiesSections) {
      try {
        const savedSections = JSON.parse(decodeURIComponent(cookiesSections.split('=')[1]));
        setSectionsOuvertes(savedSections);
      } catch (e) {
        console.error('Erreur lecture cookies:', e);
      }
    }

    // Charger les villes récentes depuis localStorage
    const savedVilles = localStorage.getItem('rk_villes_recentes');
    if (savedVilles) {
      try {
        setVillesRecentes(JSON.parse(savedVilles));
      } catch (e) {
        console.error('Erreur lecture villes récentes:', e);
      }
    }

    // Charger l'historique des rapports
    const savedHistorique = localStorage.getItem('rk_historique_rapports');
    if (savedHistorique) {
      try {
        setHistoriqueRapports(JSON.parse(savedHistorique));
      } catch (e) {
        console.error('Erreur lecture historique:', e);
      }
    }

    // Charger la dernière sauvegarde auto
    const autoSave = localStorage.getItem('rk_autosave');
    if (autoSave) {
      try {
        const data = JSON.parse(autoSave);
        // Demander si on veut restaurer
        if (window.confirm('Une sauvegarde automatique a été trouvée. Voulez-vous la restaurer ?')) {
          setSelectedVille(data.selectedVille || '');
          setListeVillageoiseJour(data.listeVillageoiseJour || '');
          setListeVillageoiseVeille(data.listeVillageoiseVeille || '');
          setListeGroupes(data.listeGroupes || '');
          setVisions(data.visions || '');
          setEvenements(data.evenements || '');
          setRapportPrevot(data.rapportPrevot || '');
          setListeVilains(data.listeVilains || '');
          setVilainsRecherches(data.vilainsRecherches || '');
          setAnalyseIntercesseur(data.analyseIntercesseur || '');
        }
      } catch (e) {
        console.error('Erreur restauration auto-save:', e);
      }
    }
  }, []);

  // Sauvegarder dans les cookies à chaque changement
  useEffect(() => {
    const expires = new Date();
    expires.setFullYear(expires.getFullYear() + 1); // Cookie valide 1 an
    document.cookie = `rk_sections=${encodeURIComponent(JSON.stringify(sectionsOuvertes))}; expires=${expires.toUTCString()}; path=/`;
  }, [sectionsOuvertes]);

  // Auto-save toutes les 30 secondes
  useEffect(() => {
    if (!autoSaveEnabled) return;

    const interval = setInterval(() => {
      const dataToSave = {
        selectedVille,
        listeVillageoiseJour,
        listeVillageoiseVeille,
        listeGroupes,
        visions,
        evenements,
        rapportPrevot,
        listeVilains,
        vilainsRecherches,
        analyseIntercesseur,
        timestamp: new Date().toISOString()
      };

      // Ne sauvegarder que si au moins un champ est rempli
      const hasData = Object.values(dataToSave).some(v => v && v !== '');
      if (hasData) {
        localStorage.setItem('rk_autosave', JSON.stringify(dataToSave));
      }
    }, 30000); // 30 secondes

    return () => clearInterval(interval);
  }, [autoSaveEnabled, selectedVille, listeVillageoiseJour, listeVillageoiseVeille, 
      listeGroupes, visions, evenements, rapportPrevot, listeVilains, 
      vilainsRecherches, analyseIntercesseur]);

  // Preview en temps réel des mouvements
  useEffect(() => {
    if (!listeVillageoiseJour) {
      setPreviewMouvements(null);
      return;
    }

    const villageoiseJour = listeVillageoiseJour.split('\n').map(v => v.trim()).filter(v => v);
    const villageoiseVeille = listeVillageoiseVeille.split('\n').map(v => v.trim()).filter(v => v);

    const arrivees = villageoiseJour.filter(v => !villageoiseVeille.includes(v));
    const departs = villageoiseVeille.filter(v => !villageoiseJour.includes(v));

    setPreviewMouvements({
      totalJour: villageoiseJour.length,
      totalVeille: villageoiseVeille.length,
      arrivees: arrivees.length,
      departs: departs.length,
      variation: villageoiseJour.length - villageoiseVeille.length
    });
  }, [listeVillageoiseJour, listeVillageoiseVeille]);

  // Raccourcis clavier
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Ctrl+Enter : Analyser
      if (e.ctrlKey && e.key === 'Enter') {
        e.preventDefault();
        if (selectedVille && listeVillageoiseJour) {
          analyserMouvements();
        }
      }
      
      // Ctrl+S : Copier le rapport
      if (e.ctrlKey && e.key === 's') {
        e.preventDefault();
        if (resultats) {
          exporterRapport();
        }
      }
      
      // Ctrl+E : Exporter JSON
      if (e.ctrlKey && e.key === 'e') {
        e.preventDefault();
        exporterJSON();
      }
      
      // Ctrl+R : Réinitialiser
      if (e.ctrlKey && e.key === 'r') {
        e.preventDefault();
        if (window.confirm('Voulez-vous vraiment réinitialiser le formulaire ?')) {
          reinitialiser();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedVille, listeVillageoiseJour, resultats]);

  // Fonction pour toggler une section
  const toggleSection = (section) => {
    setSectionsOuvertes(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Fonction pour nettoyer et parser automatiquement les listes
  const nettoyerListe = (texte) => {
    return texte
      .split('\n')
      .map(ligne => ligne.trim())
      .filter(ligne => ligne && ligne.length > 0)
      .filter((ligne, index, self) => self.indexOf(ligne) === index) // Supprimer les doublons
      .join('\n');
  };

  // Fonction pour compter les entrées dans une liste
  const compterEntrees = (texte) => {
    if (!texte) return 0;
    return texte.split('\n').filter(ligne => ligne.trim()).length;
  };

  // Fonction pour ajouter une ville aux récentes
  const ajouterVilleRecente = (ville) => {
    if (!ville) return;
    
    const nouvelles = [ville, ...villesRecentes.filter(v => v !== ville)].slice(0, 5);
    setVillesRecentes(nouvelles);
    localStorage.setItem('rk_villes_recentes', JSON.stringify(nouvelles));
  };

  // Fonction pour sauvegarder dans l'historique
  const sauvegarderDansHistorique = (rapport) => {
    const nouvelHistorique = [
      {
        id: Date.now(),
        date: rapport.date,
        ville: rapport.ville,
        timestamp: new Date().toISOString(),
        data: {
          selectedVille: rapport.ville,
          listeVillageoiseJour,
          listeVillageoiseVeille,
          listeGroupes,
          visions,
          evenements,
          rapportPrevot,
          listeVilains,
          vilainsRecherches,
          analyseIntercesseur
        }
      },
      ...historiqueRapports
    ].slice(0, 10); // Garder seulement les 10 derniers

    setHistoriqueRapports(nouvelHistorique);
    localStorage.setItem('rk_historique_rapports', JSON.stringify(nouvelHistorique));
  };

  // Fonction pour charger un rapport depuis l'historique
  const chargerRapport = (rapportData) => {
    if (!rapportData || !rapportData.data) return;

    const data = rapportData.data;
    setSelectedVille(data.selectedVille || '');
    setListeVillageoiseJour(data.listeVillageoiseJour || '');
    setListeVillageoiseVeille(data.listeVillageoiseVeille || '');
    setListeGroupes(data.listeGroupes || '');
    setVisions(data.visions || '');
    setEvenements(data.evenements || '');
    setRapportPrevot(data.rapportPrevot || '');
    setListeVilains(data.listeVilains || '');
    setVilainsRecherches(data.vilainsRecherches || '');
    setAnalyseIntercesseur(data.analyseIntercesseur || '');

    afficherNotification(`📋 Rapport du ${rapportData.date} chargé !`, 'success');
  };

  // Fonction pour exporter les données en JSON
  const exporterJSON = () => {
    const data = {
      version: '1.0',
      exportDate: new Date().toISOString(),
      selectedVille,
      listeVillageoiseJour,
      listeVillageoiseVeille,
      listeGroupes,
      visions,
      evenements,
      rapportPrevot,
      listeVilains,
      vilainsRecherches,
      analyseIntercesseur
    };

    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `RK_Export_${selectedVille || 'Donnees'}_${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    afficherNotification('💾 Données exportées en JSON !', 'success');
  };

  // Fonction pour importer des données depuis JSON
  const importerJSON = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = JSON.parse(e.target.result);
        
        setSelectedVille(data.selectedVille || '');
        setListeVillageoiseJour(data.listeVillageoiseJour || '');
        setListeVillageoiseVeille(data.listeVillageoiseVeille || '');
        setListeGroupes(data.listeGroupes || '');
        setVisions(data.visions || '');
        setEvenements(data.evenements || '');
        setRapportPrevot(data.rapportPrevot || '');
        setListeVilains(data.listeVilains || '');
        setVilainsRecherches(data.vilainsRecherches || '');
        setAnalyseIntercesseur(data.analyseIntercesseur || '');

        afficherNotification('📥 Données importées avec succès !', 'success');
      } catch (error) {
        afficherNotification('❌ Erreur lors de l\'import du fichier JSON', 'error');
        console.error('Erreur import JSON:', error);
      }
    };
    reader.readAsText(file);
  };

  // Données des provinces et villes
  const provinces = {
    'Limousin et Marche': ['Gueret', 'Limoges', 'Bourganeuf', 'Tulle', 'Ventadour', 'Rochechouart'],
    'Périgord': ['Perigueux', 'Sarlat', 'Castillon', 'Bergerac'],
    'Angoumois et Saintonge': ['Angouleme', 'Saintes'],
    'Poitou': ['Poitiers', 'La Tremouille', 'Niort', 'Thouars', 'La Rochelle'],
    'Berry': ['Bourges', 'Chateauroux', 'St Aignan', 'Sancerre'],
    'Auvergne': ['Clermont', 'Aurillac', 'Murat', 'Thiers', 'Montpensier', 'Montlucon', 'Bourbon', 'Montbrisson', 'Moulins', 'Polignac'],
    'Guyenne et Gascogne': ['Bordeaux', 'La Teste de Buch', 'Bazas', 'Blaye', 'Marmande', 'Agen', 'Montauban', 'Cahors', 'Ste Illinda'],
    'Bourgogne': ['Dijon', 'Tonnerre', 'Cosne', 'Chalon', 'Macon', 'Nevers', 'Autun', 'Semur', 'Joinville'],
    'Rouergue': ['Rodez', 'Villefranche de R.', 'Espalion', 'Millau'],
    'Languedoc': ['Toulouse', 'Foix', 'Castelnaudry', 'Albi', 'Castres'],
    'Comté d\'Armagnac': ['Auch', 'St Bertrand de Comm.', 'Muret', 'St Liziers', 'Eauze', 'Lectoure'],
    'Béarn': ['Pau', 'Tarbes', 'Lourdes', 'Mauleon', 'Orthez'],
    'Landes': ['Mt de Marsan', 'Labrit', 'Dax', 'Mimizan', 'Bayonne'],
    'Provence': ['Montpellier', 'Nimes', 'Beziers', 'Lodeve', 'Narbonne', 'Carcassonne', 'Uzes', 'Alais', 'Mende'],
    'Lyonnais et Dauphiné': ['Lyon', 'Vienne', 'Valence', 'Montelimar', 'Die', 'Embrun', 'Briancon'],
    'Touraine': ['Tours', 'Vendome', 'Loches', 'Chinon'],
    'Orléanais': ['Orleans', 'Patay', 'Blois', 'Gien', 'Montargis'],
    'Maine': ['Le Mans', 'Laval', 'Mayenne', 'Montmirail'],
    'Normandie': ['Alencon', 'Argentan', 'Verneuil', 'Mortagne', 'Honfleur', 'Lisieux', 'Bayeux', 'Avranches', 'Fecamp', 'Dieppe'],
    'Champagne': ['Reims', 'Ste Menehould', 'Compiegne', 'Argonne', 'Troyes', 'Conflans les Sens', 'Clermont', 'Varennes', 'Langres'],
    'Anjou': ['Angers', 'Craon', 'Saumur', 'La Fleche'],
    'Artois et Flandres': ['Arras', 'Azincourt', 'Bertincourt', 'Calais', 'Cambrai', 'Peronne', 'Tastevin'],
    'Comté de Flandre': ['Bruges', 'Dunkerque', 'Gent', 'Antverpen', 'Tournai'],
    'Méditerranée': ['Aix', 'Marseille', 'Arles', 'Brignoles'],
    'Franche-Comté': ['Dole', 'Vesoul', 'Luxeuil', 'Pontarlier', 'Poligny', 'St Claude'],
    'Lorraine': ['Nancy', 'Vaudemont', 'Epinal', 'Toul'],
    'Savoie': ['Chambery', 'Annecy', 'Belley', 'Bourg'],
    'Suisse': ['Fribourg', 'Geneve', 'Sion', 'Grandson', 'Solothurn', 'Luzern', 'Schwyz'],
    'Souabe': ['Stuttgart', 'Heilbronn', 'Esslingen', 'Reutlingen', 'Ulm', 'Zwiefalten', 'Rottweil', 'Zollern'],
    'Bade': ['Baden', 'Offenburg', 'Freiburg', 'Lorrach'],
    'Bavière Sud': ['Augsburg', 'Memmingen', 'Ravensburg', 'Lindau', 'Konstanz', 'Schaffhausen'],
    'Franconie': ['Nurnberg', 'Ansbach', 'Eichstatt', 'Rothenburg', 'Schwabisch hall', 'Heiligenbronn'],
    'Rhénanie': ['Mainz', 'Frankfurt', 'Aschaffenburg', 'Buchen', 'Bad Mergentheim'],
    'Bavière': ['Munchen', 'Freising', 'Ingolstadt', 'Regensburg', 'Deggendorf', 'Passau', 'Landshut'],
    'Autriche': ['Wien', 'Ternitz', 'Mistelbach', 'Linz', 'Amstetten'],
    'Styrie': ['Graz', 'Marburg an der Drau', 'Rottenmann', 'Bruck an der Mur'],
    'Lombardie': ['Milano', 'Novara', 'Como', 'Lodi', 'Pavia', 'Alessandria', 'Piacenza', 'Parma', 'Fornovo'],
    'Ligurie': ['Genova', 'Chiavari', 'Savona', 'Albenga', 'Ventimiglia', 'La Spezia'],
    'Émilie': ['Modena', 'Massa', 'Guastalla', 'Mirandola', 'Mantoue'],
    'Toscane': ['Firenze', 'Pistoia', 'San Miniato', 'Pisa', 'Livorno', 'Volterra', 'Piombino', 'Montevarchi', 'Arezzo', 'Montepulciano', 'Valdiponte'],
    'Sienne': ['Siena', 'Grosseto', 'Orbetello', 'Santa Fiora'],
    'Abruzzes': ['Aquila', 'Tagiacozzo', 'Avezzano', 'Sulmona', 'Chieti', 'Silvi', 'Teramo'],
    'Campanie': ['Capua', 'Sessa Arunca', 'Gaeta', 'Terracina', 'Pontecorvo', 'Sora'],
    'Vénétie': ['Venezia', 'Padova', 'Verona', 'Treviso', 'Portogruaro', 'Udine', 'Parenzo', 'Pola']
  };

  // Fonction pour afficher une notification
  const afficherNotification = (message, type = 'error') => {
    const notification = document.createElement('div');
    notification.innerHTML = type === 'error' ? `❌ ${message}` : `✅ ${message}`;
    notification.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: ${type === 'error' ? 'linear-gradient(135deg, #ff0000, #cc0000)' : 'linear-gradient(135deg, #00ff00, #00cc00)'};
      color: #fff;
      padding: 15px 25px;
      border-radius: 10px;
      font-weight: bold;
      z-index: 9999;
      box-shadow: 0 5px 20px ${type === 'error' ? 'rgba(255, 0, 0, 0.5)' : 'rgba(0, 255, 0, 0.5)'};
      animation: slideIn 0.3s ease-out;
      max-width: 400px;
    `;
    document.body.appendChild(notification);
    
    setTimeout(() => {
      notification.style.animation = 'slideOut 0.3s ease-out';
      setTimeout(() => notification.remove(), 300);
    }, 4000);
  };

  // Fonction pour convertir la date réelle en date du jeu (1474)
  const convertirDateJeu = () => {
    const dateReelle = new Date();
    // 2026 dans la réalité = 1474 dans le jeu
    const anneeJeu = 1474;
    const moisFr = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 
                    'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];
    
    const jour = dateReelle.getDate();
    const mois = moisFr[dateReelle.getMonth()];
    
    return `${jour} ${mois} ${anneeJeu}`;
  };

  // Fonction pour analyser les listes et détecter les mouvements
  const analyserMouvements = () => {
    // Validation des champs obligatoires
    if (!selectedVille) {
      afficherNotification('Veuillez sélectionner une ville !', 'error');
      return;
    }

    if (!listeVillageoiseJour || listeVillageoiseJour.trim() === '') {
      afficherNotification('Veuillez fournir la liste des villageois du jour !', 'error');
      return;
    }

    // Parse les listes
    const villageoiseJour = listeVillageoiseJour.split('\n').map(v => v.trim()).filter(v => v);
    const villageoiseVeille = listeVillageoiseVeille.split('\n').map(v => v.trim()).filter(v => v);
    const groupesListe = listeGroupes.split('\n').map(g => g.trim()).filter(g => g);
    const vilainsLocaux = listeVilains.split('\n').map(v => v.trim()).filter(v => v);
    
    // Supprimer les doublons dans la liste des vilains recherchés
    const recherches = [...new Set(vilainsRecherches.split('\n').map(v => v.trim()).filter(v => v))];

    // Calculer les mouvements
    const arrivees = villageoiseJour.filter(v => !villageoiseVeille.includes(v));
    const departs = villageoiseVeille.filter(v => !villageoiseJour.includes(v));
    const presents = villageoiseJour.filter(v => villageoiseVeille.includes(v));

    // Détecter les vilains recherchés (correspondances partielles insensibles à la casse)
    const detectes = [];
    
    // Fonction pour normaliser les noms (enlever accents, minuscules)
    const normaliser = (str) => str.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
    
    recherches.forEach(recherche => {
      const rechercheNorm = normaliser(recherche);
      
      // Chercher dans les villageois présents
      villageoiseJour.forEach(villageois => {
        if (normaliser(villageois).includes(rechercheNorm)) {
          detectes.push({
            type: 'Villageois présent',
            nom: villageois,
            recherche: recherche,
            icon: '👤'
          });
        }
      });
      
      // Chercher dans les arrivées
      arrivees.forEach(arrive => {
        if (normaliser(arrive).includes(rechercheNorm)) {
          detectes.push({
            type: 'Nouvelle arrivée',
            nom: arrive,
            recherche: recherche,
            icon: '🚨'
          });
        }
      });
      
      // Chercher dans les vilains locaux
      vilainsLocaux.forEach(vilain => {
        if (normaliser(vilain).includes(rechercheNorm)) {
          detectes.push({
            type: 'Vilain signalé localement',
            nom: vilain,
            recherche: recherche,
            icon: '⚠️'
          });
        }
      });
      
      // Chercher dans les groupes
      groupesListe.forEach(groupe => {
        if (normaliser(groupe).includes(rechercheNorm)) {
          detectes.push({
            type: 'Groupe suspect',
            nom: groupe,
            recherche: recherche,
            icon: '🛡️'
          });
        }
      });
    });

    setVilainsDetectes(detectes);

    const rapportFinal = {
      ville: selectedVille,
      totalJour: villageoiseJour.length,
      totalVeille: villageoiseVeille.length,
      villageoiseJour: villageoiseJour,
      arrivees: arrivees,
      departs: departs,
      presents: presents,
      groupes: groupesListe,
      visions: visions,
      evenements: evenements,
      rapportPrevot: rapportPrevot,
      listeVilains: vilainsLocaux,
      vilainsRecherches: recherches,
      vilainsDetectes: detectes,
      analyseIntercesseur: analyseIntercesseur,
      date: convertirDateJeu()
    };

    setResultats(rapportFinal);

    // Ajouter la ville aux récentes
    ajouterVilleRecente(selectedVille);

    // Sauvegarder dans l'historique
    sauvegarderDansHistorique(rapportFinal);

    // Notification de succès
    afficherNotification('✅ Analyse terminée avec succès !', 'success');
    
    // Alerte spéciale si des vilains recherchés sont détectés
    if (detectes.length > 0) {
      setTimeout(() => {
        afficherNotification(`🚨 ALERTE : ${detectes.length} vilain(s) recherché(s) détecté(s) !`, 'error');
      }, 500);
    }
  };

  const reinitialiser = () => {
    setSelectedVille('');
    setListeVillageoiseJour('');
    setListeVillageoiseVeille('');
    setListeGroupes('');
    setVisions('');
    setEvenements('');
    setRapportPrevot('');
    setListeVilains('');
    setVilainsRecherches('');
    setAnalyseIntercesseur('');
    setResultats(null);
    setVilainsDetectes([]);
    afficherNotification('🔄 Formulaire réinitialisé', 'success');
  };

  const exporterRapport = async () => {
    if (!resultats) {
      afficherNotification('Aucun rapport à exporter ! Veuillez d\'abord analyser les mouvements.', 'error');
      return;
    }

    const variation = resultats.totalJour - resultats.totalVeille;
    const variationText = variation > 0 ? `[color=green]+${variation}[/color]` : variation < 0 ? `[color=red]${variation}[/color]` : '0';

    const rapport = `[center][size=18][color=orange]⚜️ RAPPORT DE CONTRÔLE DES FRONTIÈRES ⚜️[/color][/size][/center]

[b]📅 Date du rapport :[/b] ${resultats.date}
[b]🏰 Ville contrôlée :[/b] [size=14][color=orange]${resultats.ville}[/color][/size]

[hr]

[center][size=16][b][color=blue]📊 STATISTIQUES GÉNÉRALES[/color][/b][/size][/center]

[b]👥 Population actuelle :[/b] [b][color=green]${resultats.totalJour}[/color][/b] personne(s)
[b]📈 Population précédente :[/b] ${resultats.totalVeille} personne(s)
[b]📊 Variation :[/b] [b]${variationText}[/b]

[hr]

[center][size=15][b][color=blue]👥 HABITANTS PRÉSENTS (${resultats.totalJour})[/color][/b][/size][/center]

${resultats.villageoiseJour && resultats.villageoiseJour.length > 0 ? 
  resultats.villageoiseJour.map(v => `[char]${v}[/char]`).join(' ') : 
  '[i]Aucun habitant listé[/i]'}

[hr]

[center][size=15][b][color=green]✅ ARRIVÉES (${resultats.arrivees.length})[/color][/b][/size][/center]

${resultats.arrivees.length > 0 ? 
  resultats.arrivees.map(v => `[color=green]▸[/color] [char]${v}[/char]`).join('\n') : 
  '[i]Aucune arrivée détectée[/i]'}

[hr]

[center][size=15][b][color=red]❌ DÉPARTS (${resultats.departs.length})[/color][/b][/size][/center]

${resultats.departs.length > 0 ? 
  resultats.departs.map(v => `[color=red]▸[/color] [char]${v}[/char]`).join('\n') : 
  '[i]Aucun départ détecté[/i]'}

${resultats.groupes.length > 0 ? `
[hr]

[center][size=15][b][color=purple]🛡️ GROUPES ET ARMÉES (${resultats.groupes.length})[/color][/b][/size][/center]

${resultats.groupes.map(g => `[color=purple]▸[/color] ${g}`).join('\n')}` : ''}

${resultats.visions ? `
[hr]

[center][size=15][b][color=blue]👁️ VISIONS ET OBSERVATIONS[/color][/b][/size][/center]

[quote]${resultats.visions}[/quote]` : ''}

${resultats.evenements ? `
[hr]

[center][size=15][b][color=orange]📰 ÉVÉNEMENTS NOTABLES[/color][/b][/size][/center]

[quote]${resultats.evenements}[/quote]` : ''}

${resultats.rapportPrevot || resultats.listeVilains.length > 0 ? `
[hr]

[center][size=16][b][color=darkred]⚖️ RAPPORT DU PRÉVÔT[/color][/b][/size][/center]` : ''}

${resultats.rapportPrevot ? `
[b][color=darkred]📋 Synthèse du jour :[/color][/b]
[quote]${resultats.rapportPrevot}[/quote]
` : ''}

${resultats.listeVilains.length > 0 ? `
[b][color=darkred]🚨 Liste des vilains :[/color][/b]
${resultats.listeVilains.map((vilain, i) => `[color=darkred]▸[/color] [b]${vilain}[/b]`).join('\n')}` : ''}

${resultats.vilainsRecherches.length > 0 || resultats.analyseIntercesseur || resultats.vilainsDetectes.length > 0 ? `
[hr]

[center][size=16][b][color=maroon]⚜️ GRANDE PRÉVÔTÉ DE FRANCE[/color][/b][/size]
[size=12][i]Intercesseurs Royaux[/i][/size][/center]` : ''}

${resultats.vilainsDetectes.length > 0 ? `
[b][color=maroon]🚨 ALERTE - VILAINS RECHERCHÉS DÉTECTÉS ![/color][/b]
${resultats.vilainsDetectes.map((d, i) => `[color=maroon]${d.icon}[/color] [b]${d.nom}[/b] - ${d.type} [i](Recherché: ${d.recherche})[/i]`).join('\n')}
` : ''}

${resultats.vilainsRecherches.length > 0 && resultats.vilainsDetectes.length === 0 ? `
[b][color=green]✓ Aucun vilain recherché détecté dans cette ville[/color][/b]
[i]Liste de surveillance : ${resultats.vilainsRecherches.join(', ')}[/i]
` : ''}

${resultats.analyseIntercesseur ? `
[b][color=maroon]📜 Analyse de l'Intercesseur :[/color][/b]
[quote]${resultats.analyseIntercesseur}[/quote]` : ''}

[hr]

[center][size=10][i]Rapport généré par l'outil de Contrôle des Frontières - Site Unofficiel pour Renaissance Kingdoms[/i][/center]`;

    try {
      // Méthode 1 : API Clipboard moderne (HTTPS requis)
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(rapport);
        afficherNotification('📋 Rapport copié dans le presse-papier !', 'success');
      } else {
        // Méthode 2 : Fallback pour HTTP (sans HTTPS)
        const textArea = document.createElement('textarea');
        textArea.value = rapport;
        textArea.style.position = 'fixed';
        textArea.style.left = '-9999px';
        textArea.style.top = '-9999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        const success = document.execCommand('copy');
        document.body.removeChild(textArea);
        if (success) {
          afficherNotification('📋 Rapport copié dans le presse-papier !', 'success');
        } else {
          throw new Error('execCommand failed');
        }
      }
    } catch (err) {
      console.error('Erreur lors de la copie:', err);
      // Méthode 3 : Ouvrir dans un nouvel onglet pour copie manuelle
      const win = window.open('', '_blank');
      if (win) {
        win.document.write('<pre style="white-space:pre-wrap;font-family:monospace;padding:20px;">' + rapport.replace(/</g, '&lt;').replace(/>/g, '&gt;') + '</pre>');
        win.document.title = 'Rapport RK Douanes - Copie manuelle';
        afficherNotification('📋 Rapport ouvert dans un nouvel onglet ! Sélectionne tout (Ctrl+A) puis copie (Ctrl+C)', 'success');
      } else {
        afficherNotification('❌ Erreur lors de la copie. Activez HTTPS pour résoudre ce problème.', 'error');
      }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 text-white p-4 md:p-8">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cinzel:wght@400;600;700&family=Crimson+Text:ital,wght@0,400;0,600;1,400&display=swap');
        
        .title-font { font-family: 'Cinzel', serif; }
        .body-font { font-family: 'Crimson Text', serif; }
        
        .medieval-border {
          border: 3px solid;
          border-image: linear-gradient(45deg, #d4af37, #f4e4c1, #d4af37) 1;
          box-shadow: 0 0 20px rgba(212, 175, 55, 0.3);
        }
        
        .scroll-shadow {
          box-shadow: 
            inset 0 10px 20px rgba(0,0,0,0.3),
            0 5px 15px rgba(0,0,0,0.5);
          background: linear-gradient(to bottom, 
            rgba(255,255,255,0.05) 0%,
            rgba(255,255,255,0.02) 50%,
            rgba(0,0,0,0.1) 100%);
        }
        
        .gold-text {
          background: linear-gradient(45deg, #d4af37, #f4e4c1, #d4af37);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .stats-card {
          background: linear-gradient(135deg, rgba(99, 102, 241, 0.1), rgba(168, 85, 247, 0.1));
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.1);
          transition: all 0.3s ease;
        }

        .stats-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 10px 30px rgba(168, 85, 247, 0.3);
        }

        textarea, select, input {
          background: rgba(0, 0, 0, 0.3);
          border: 1px solid rgba(255, 255, 255, 0.2);
          transition: all 0.3s ease;
        }

        textarea:focus, select:focus, input:focus {
          outline: none;
          border-color: #d4af37;
          box-shadow: 0 0 10px rgba(212, 175, 55, 0.3);
        }

        .btn-primary {
          background: linear-gradient(135deg, #d4af37, #f4e4c1);
          color: #1e1b4b;
          transition: all 0.3s ease;
        }

        .btn-primary:hover {
          transform: scale(1.05);
          box-shadow: 0 5px 20px rgba(212, 175, 55, 0.5);
        }

        .btn-secondary {
          background: linear-gradient(135deg, rgba(99, 102, 241, 0.3), rgba(168, 85, 247, 0.3));
          transition: all 0.3s ease;
        }

        .btn-secondary:hover {
          background: linear-gradient(135deg, rgba(99, 102, 241, 0.5), rgba(168, 85, 247, 0.5));
          transform: scale(1.05);
        }

        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .fade-in {
          animation: fadeIn 0.5s ease-out;
        }

        @keyframes slideIn {
          from { transform: translateX(400px); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }

        @keyframes slideOut {
          from { transform: translateX(0); opacity: 1; }
          to { transform: translateX(400px); opacity: 0; }
        }

        .accordion-header {
          cursor: pointer;
          transition: all 0.3s ease;
          user-select: none;
        }

        .accordion-header:hover {
          background: rgba(255, 255, 255, 0.05);
        }

        .accordion-content {
          max-height: 0;
          overflow: hidden;
          transition: max-height 0.3s ease-out, opacity 0.3s ease-out;
          opacity: 0;
        }

        .accordion-content.open {
          max-height: 5000px;
          opacity: 1;
          transition: max-height 0.5s ease-in, opacity 0.3s ease-in;
        }

        .chevron-icon {
          transition: transform 0.3s ease;
        }

        .chevron-icon.rotated {
          transform: rotate(180deg);
        }
      `}</style>

      {/* En-tête */}
      <header className="text-center mb-12 fade-in">
        <div className="inline-block medieval-border p-6 bg-black bg-opacity-30 rounded-lg mb-4">
          <h1 className="title-font text-4xl md:text-6xl font-bold gold-text mb-2">
            DOUANES
          </h1>
          <p className="title-font text-xl md:text-2xl text-purple-300">
            Renaissance Kingdoms
          </p>
        </div>
        <p className="body-font text-lg text-gray-300 max-w-2xl mx-auto">
          Contrôle des Frontières et Surveillance des Mouvements
        </p>
      </header>

      {/* Barre d'outils : Villes récentes et Historique */}
      <div className="max-w-6xl mx-auto mb-6 fade-in">
        <div className="grid md:grid-cols-2 gap-4">
          {/* Villes récentes */}
          {villesRecentes.length > 0 && (
            <div className="bg-gradient-to-r from-blue-900/30 to-purple-900/30 rounded-lg p-4 border border-blue-500/30">
              <h3 className="title-font text-sm text-blue-300 mb-2 flex items-center gap-2">
                <Clock className="w-4 h-4" />
                Villes récentes
              </h3>
              <div className="flex flex-wrap gap-2">
                {villesRecentes.map((ville, i) => (
                  <button
                    key={i}
                    onClick={() => setSelectedVille(ville)}
                    className="px-3 py-1 bg-blue-600 hover:bg-blue-500 rounded-full text-sm body-font transition-all"
                  >
                    {ville}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Historique des rapports */}
          {historiqueRapports.length > 0 && (
            <div className="bg-gradient-to-r from-purple-900/30 to-pink-900/30 rounded-lg p-4 border border-purple-500/30">
              <h3 className="title-font text-sm text-purple-300 mb-2 flex items-center gap-2">
                <History className="w-4 h-4" />
                Historique ({historiqueRapports.length})
              </h3>
              <select
                onChange={(e) => {
                  if (e.target.value) {
                    const rapport = historiqueRapports.find(r => r.id === parseInt(e.target.value));
                    if (rapport) chargerRapport(rapport);
                  }
                }}
                className="w-full p-2 rounded bg-black/30 text-white border border-purple-500/50 body-font text-sm"
              >
                <option value="">Charger un rapport...</option>
                {historiqueRapports.map((rapport) => (
                  <option key={rapport.id} value={rapport.id}>
                    {rapport.ville} - {rapport.date}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        {/* Boutons Import/Export */}
        <div className="flex flex-wrap gap-3 mt-4 justify-center">
          <button
            onClick={exporterJSON}
            className="btn-secondary px-4 py-2 rounded-lg font-bold title-font text-sm flex items-center gap-2"
          >
            <FileJson className="w-4 h-4" />
            Exporter JSON
          </button>
          
          <label className="btn-secondary px-4 py-2 rounded-lg font-bold title-font text-sm flex items-center gap-2 cursor-pointer">
            <Upload className="w-4 h-4" />
            Importer JSON
            <input
              type="file"
              accept=".json"
              onChange={importerJSON}
              className="hidden"
            />
          </label>

          <button
            onClick={() => setAutoSaveEnabled(!autoSaveEnabled)}
            className={`px-4 py-2 rounded-lg font-bold title-font text-sm flex items-center gap-2 transition-all ${
              autoSaveEnabled ? 'bg-green-600 hover:bg-green-500' : 'bg-gray-600 hover:bg-gray-500'
            }`}
          >
            <Clock className="w-4 h-4" />
            Auto-save {autoSaveEnabled ? 'ON' : 'OFF'}
          </button>
        </div>

        {/* Raccourcis clavier */}
        <div className="mt-3 text-center body-font text-xs text-gray-400">
          💡 Raccourcis : <kbd className="bg-gray-700 px-1 rounded">Ctrl+Enter</kbd> Analyser | 
          <kbd className="bg-gray-700 px-1 rounded ml-1">Ctrl+S</kbd> Copier rapport | 
          <kbd className="bg-gray-700 px-1 rounded ml-1">Ctrl+E</kbd> Export JSON
        </div>
      </div>

      {/* Formulaire principal */}
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Sélection de la ville */}
        <div className="medieval-border scroll-shadow rounded-lg p-6 fade-in">
          <div 
            className="accordion-header flex items-center justify-between"
            onClick={() => toggleSection('ville')}
          >
            <label className="title-font text-xl text-yellow-300 flex items-center gap-2">
              <MapPin className="w-6 h-6" />
              Sélectionner une ville
            </label>
            {sectionsOuvertes.ville ? 
              <ChevronUp className="w-6 h-6 text-yellow-300 chevron-icon" /> : 
              <ChevronDown className="w-6 h-6 text-yellow-300 chevron-icon" />
            }
          </div>
          <div className={`accordion-content ${sectionsOuvertes.ville ? 'open' : ''}`}>
            <div className="mt-4">
              <select
                value={selectedVille}
                onChange={(e) => setSelectedVille(e.target.value)}
                className="w-full p-3 rounded-lg body-font text-lg text-white"
              >
                <option value="">-- Choisir une ville --</option>
                {Object.entries(provinces).map(([province, villes]) => (
                  <optgroup key={province} label={province}>
                    {villes.map(ville => (
                      <option key={ville} value={ville}>{ville}</option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Listes des villageois */}
        <div className="medieval-border scroll-shadow rounded-lg p-6 fade-in">
          <div 
            className="accordion-header flex items-center justify-between"
            onClick={() => toggleSection('villageois')}
          >
            <h3 className="title-font text-xl text-yellow-300 flex items-center gap-2">
              <Users className="w-6 h-6" />
              Listes des villageois
            </h3>
            {sectionsOuvertes.villageois ? 
              <ChevronUp className="w-6 h-6 text-yellow-300 chevron-icon" /> : 
              <ChevronDown className="w-6 h-6 text-yellow-300 chevron-icon" />
            }
          </div>
          <div className={`accordion-content ${sectionsOuvertes.villageois ? 'open' : ''}`}>
            <div className="grid md:grid-cols-2 gap-6 mt-4">
              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="title-font text-lg text-yellow-200">
                    Liste villageois du jour *
                  </label>
                  <span className="text-sm text-green-400 font-bold">
                    {compterEntrees(listeVillageoiseJour)} noms
                  </span>
                </div>
                <p className="body-font text-sm text-gray-400 mb-2">
                  Coller la liste depuis: Mairie → Informations sur le village
                </p>
                <textarea
                  value={listeVillageoiseJour}
                  onChange={(e) => setListeVillageoiseJour(e.target.value)}
                  onBlur={(e) => setListeVillageoiseJour(nettoyerListe(e.target.value))}
                  className="w-full h-40 p-3 rounded-lg body-font text-white resize-none"
                  placeholder="Un nom par ligne..."
                />
                <button
                  onClick={() => setListeVillageoiseJour(nettoyerListe(listeVillageoiseJour))}
                  className="mt-2 text-xs text-blue-400 hover:text-blue-300"
                >
                  🧹 Nettoyer la liste
                </button>
              </div>

              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="title-font text-lg text-yellow-200">
                    Liste villageois de la veille
                  </label>
                  <span className="text-sm text-blue-400 font-bold">
                    {compterEntrees(listeVillageoiseVeille)} noms
                  </span>
                </div>
                <p className="body-font text-sm text-gray-400 mb-2">
                  Facultatif - Liste précédente pour comparaison
                </p>
                <textarea
                  value={listeVillageoiseVeille}
                  onChange={(e) => setListeVillageoiseVeille(e.target.value)}
                  onBlur={(e) => setListeVillageoiseVeille(nettoyerListe(e.target.value))}
                  className="w-full h-40 p-3 rounded-lg body-font text-white resize-none"
                  placeholder="Un nom par ligne..."
                />
                <button
                  onClick={() => setListeVillageoiseVeille(nettoyerListe(listeVillageoiseVeille))}
                  className="mt-2 text-xs text-blue-400 hover:text-blue-300"
                >
                  🧹 Nettoyer la liste
                </button>
              </div>
            </div>

            {/* Preview des mouvements en temps réel */}
            {previewMouvements && previewMouvements.totalJour > 0 && (
              <div className="mt-4 p-3 bg-gradient-to-r from-green-900/20 to-red-900/20 rounded-lg border border-yellow-500/30">
                <div className="flex items-center justify-around text-sm body-font">
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4 text-green-400" />
                    <span className="text-green-400">Aujourd'hui: {previewMouvements.totalJour}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-gray-400">Hier: {previewMouvements.totalVeille}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {previewMouvements.variation > 0 ? 
                      <TrendingUp className="w-4 h-4 text-green-400" /> : 
                      previewMouvements.variation < 0 ?
                      <TrendingDown className="w-4 h-4 text-red-400" /> :
                      <span className="w-4 h-4">➡️</span>
                    }
                    <span className={previewMouvements.variation > 0 ? 'text-green-400' : previewMouvements.variation < 0 ? 'text-red-400' : 'text-gray-400'}>
                      {previewMouvements.variation > 0 ? '+' : ''}{previewMouvements.variation}
                    </span>
                  </div>
                  <div className="text-green-400">↗️ {previewMouvements.arrivees}</div>
                  <div className="text-red-400">↙️ {previewMouvements.departs}</div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Groupes et armées */}
        <div className="medieval-border scroll-shadow rounded-lg p-6 fade-in">
          <div 
            className="accordion-header flex items-center justify-between"
            onClick={() => toggleSection('groupes')}
          >
            <label className="title-font text-xl text-yellow-300 flex items-center gap-2">
              <Compass className="w-6 h-6" />
              Liste des groupes
            </label>
            {sectionsOuvertes.groupes ? 
              <ChevronUp className="w-6 h-6 text-yellow-300 chevron-icon" /> : 
              <ChevronDown className="w-6 h-6 text-yellow-300 chevron-icon" />
            }
          </div>
          <div className={`accordion-content ${sectionsOuvertes.groupes ? 'open' : ''}`}>
            <div className="mt-4">
              <div className="flex items-center justify-between mb-2">
                <p className="body-font text-sm text-gray-400">
                  Hors de la ville → Groupes et Armées
                </p>
                <span className="text-sm text-purple-400 font-bold">
                  {compterEntrees(listeGroupes)} groupes
                </span>
              </div>
              <textarea
                value={listeGroupes}
                onChange={(e) => setListeGroupes(e.target.value)}
                onBlur={(e) => setListeGroupes(nettoyerListe(e.target.value))}
                className="w-full h-32 p-3 rounded-lg body-font text-white resize-none"
                placeholder="Un groupe par ligne..."
              />
              <button
                onClick={() => setListeGroupes(nettoyerListe(listeGroupes))}
                className="mt-2 text-xs text-blue-400 hover:text-blue-300"
              >
                🧹 Nettoyer la liste
              </button>
            </div>
          </div>
        </div>

        {/* Visions et événements */}
        <div className="medieval-border scroll-shadow rounded-lg p-6 fade-in">
          <div 
            className="accordion-header flex items-center justify-between"
            onClick={() => toggleSection('visions')}
          >
            <h3 className="title-font text-xl text-yellow-300 flex items-center gap-2">
              <Eye className="w-6 h-6" />
              Visions et événements
            </h3>
            {sectionsOuvertes.visions ? 
              <ChevronUp className="w-6 h-6 text-yellow-300 chevron-icon" /> : 
              <ChevronDown className="w-6 h-6 text-yellow-300 chevron-icon" />
            }
          </div>
          <div className={`accordion-content ${sectionsOuvertes.visions ? 'open' : ''}`}>
            <div className="grid md:grid-cols-2 gap-6 mt-4">
              <div>
                <label className="title-font text-lg text-yellow-200 mb-3 block">
                  Visions
                </label>
                <textarea
                  value={visions}
                  onChange={(e) => setVisions(e.target.value)}
                  className="w-full h-32 p-3 rounded-lg body-font text-white resize-none"
                  placeholder="Rapports de visions..."
                />
              </div>

              <div>
                <label className="title-font text-lg text-yellow-200 mb-3 block">
                  Événements
                </label>
                <textarea
                  value={evenements}
                  onChange={(e) => setEvenements(e.target.value)}
                  className="w-full h-32 p-3 rounded-lg body-font text-white resize-none"
                  placeholder="Événements notables..."
                />
              </div>
            </div>
          </div>
        </div>

        {/* Rapport du Prévôt */}
        <div className="medieval-border scroll-shadow rounded-lg p-6 fade-in bg-red-900 bg-opacity-10">
          <div 
            className="accordion-header flex items-center justify-between"
            onClick={() => toggleSection('prevot')}
          >
            <div className="flex items-center gap-3">
              <div className="text-4xl">⚖️</div>
              <h3 className="title-font text-2xl text-red-300">Rapport du Prévôt</h3>
            </div>
            {sectionsOuvertes.prevot ? 
              <ChevronUp className="w-6 h-6 text-red-300 chevron-icon" /> : 
              <ChevronDown className="w-6 h-6 text-red-300 chevron-icon" />
            }
          </div>
          
          <div className={`accordion-content ${sectionsOuvertes.prevot ? 'open' : ''}`}>
            <div className="space-y-6 mt-4">
              <div>
                <label className="title-font text-lg text-red-200 mb-3 block">
                  📋 Synthèse des rapports du jour
                </label>
                <textarea
                  value={rapportPrevot}
                  onChange={(e) => setRapportPrevot(e.target.value)}
                  className="w-full h-40 p-3 rounded-lg body-font text-white resize-none"
                  placeholder="Résumé des délits, amendes, procès du jour..."
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="title-font text-lg text-red-200">
                    🚨 Liste des vilains
                  </label>
                  <span className="text-sm text-red-400 font-bold">
                    {compterEntrees(listeVilains)} noms
                  </span>
                </div>
                <p className="body-font text-sm text-gray-400 mb-2">
                  Un nom par ligne (prisonniers, condamnés, recherchés...)
                </p>
                <textarea
                  value={listeVilains}
                  onChange={(e) => setListeVilains(e.target.value)}
                  onBlur={(e) => setListeVilains(nettoyerListe(e.target.value))}
                  className="w-full h-32 p-3 rounded-lg body-font text-white resize-none"
                  placeholder="Nom du vilain&#10;Autre vilain&#10;..."
                />
                <button
                  onClick={() => setListeVilains(nettoyerListe(listeVilains))}
                  className="mt-2 text-xs text-blue-400 hover:text-blue-300"
                >
                  🧹 Nettoyer et supprimer les doublons
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Grande Prévôté de France - Intercesseurs */}
        <div className="medieval-border scroll-shadow rounded-lg p-6 fade-in bg-purple-950 bg-opacity-30 border-purple-500">
          <div 
            className="accordion-header flex items-center justify-between"
            onClick={() => toggleSection('intercesseurs')}
          >
            <div className="flex items-center gap-3">
              <div className="text-4xl">⚜️</div>
              <div>
                <h3 className="title-font text-2xl text-purple-200">Grande Prévôté de France</h3>
                <p className="body-font text-sm text-purple-300 italic">Intercesseurs Royaux</p>
              </div>
            </div>
            {sectionsOuvertes.intercesseurs ? 
              <ChevronUp className="w-6 h-6 text-purple-300 chevron-icon" /> : 
              <ChevronDown className="w-6 h-6 text-purple-300 chevron-icon" />
            }
          </div>
          
          <div className={`accordion-content ${sectionsOuvertes.intercesseurs ? 'open' : ''}`}>
            <div className="space-y-6 mt-4">
              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="title-font text-lg text-purple-200">
                    🔍 Liste des vilains recherchés au niveau national
                  </label>
                  <span className="text-sm text-purple-400 font-bold">
                    {compterEntrees(vilainsRecherches)} noms
                  </span>
                </div>
                <p className="body-font text-sm text-gray-400 mb-2">
                  Un nom par ligne - Le système détectera automatiquement leur présence
                </p>
                <textarea
                  value={vilainsRecherches}
                  onChange={(e) => setVilainsRecherches(e.target.value)}
                  onBlur={(e) => setVilainsRecherches(nettoyerListe(e.target.value))}
                  className="w-full h-40 p-3 rounded-lg body-font text-white resize-none border-purple-500 border-opacity-50"
                  placeholder="Jean le Brigand&#10;Marie la Voleuse&#10;Pierre le Bandit&#10;..."
                />
                <button
                  onClick={() => setVilainsRecherches(nettoyerListe(vilainsRecherches))}
                  className="mt-2 text-xs text-blue-400 hover:text-blue-300"
                >
                  🧹 Nettoyer et supprimer les doublons
                </button>
              </div>

              {vilainsDetectes.length > 0 && (
                <div className="bg-red-900 bg-opacity-40 border-2 border-red-500 rounded-lg p-4">
                  <h4 className="title-font text-xl text-red-300 mb-3 flex items-center gap-2">
                    🚨 ALERTE - {vilainsDetectes.length} CORRESPONDANCE(S) DÉTECTÉE(S) !
                  </h4>
                  <div className="space-y-2">
                    {vilainsDetectes.map((detect, i) => (
                      <div key={i} className="bg-black bg-opacity-30 rounded p-3 border-l-4 border-red-500">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-2xl">{detect.icon}</span>
                          <span className="body-font font-bold text-red-200">{detect.nom}</span>
                        </div>
                        <div className="body-font text-sm text-gray-300 ml-8">
                          <span className="text-yellow-300">{detect.type}</span> - 
                          <span className="italic"> Recherché: {detect.recherche}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <label className="title-font text-lg text-purple-200 mb-3 block">
                  📜 Analyse et recommandations de l'Intercesseur
                </label>
                <textarea
                  value={analyseIntercesseur}
                  onChange={(e) => setAnalyseIntercesseur(e.target.value)}
                  className="w-full h-40 p-3 rounded-lg body-font text-white resize-none border-purple-500 border-opacity-50"
                  placeholder="Notes, analyse des mouvements suspects, recommandations aux autorités locales..."
                />
              </div>
            </div>
          </div>
        </div>

        {/* Boutons d'action */}
        <div className="flex flex-wrap gap-4 justify-center fade-in">
          <button
            onClick={analyserMouvements}
            className="btn-primary px-8 py-4 rounded-lg font-bold title-font text-lg flex items-center gap-2"
          >
            <Compass className="w-5 h-5" />
            Analyser les mouvements
          </button>
          <button
            onClick={reinitialiser}
            className="btn-secondary px-8 py-4 rounded-lg font-bold title-font text-lg text-white"
          >
            Réinitialiser
          </button>
        </div>

        {/* Résultats */}
        {resultats && (
          <div className="medieval-border scroll-shadow rounded-lg p-6 mt-8 fade-in">
            <div className="flex justify-between items-center mb-6">
              <h2 className="title-font text-3xl gold-text">
                Rapport d'analyse - {resultats.ville}
              </h2>
              <button
                onClick={exporterRapport}
                className="btn-primary px-6 py-3 rounded-lg font-bold title-font flex items-center gap-2"
              >
                <Download className="w-5 h-5" />
                Copier le rapport
              </button>
            </div>

            {/* Statistiques */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className="stats-card p-4 rounded-lg">
                <div className="text-yellow-300 text-sm title-font mb-1">Population actuelle</div>
                <div className="text-3xl font-bold">{resultats.totalJour}</div>
              </div>
              <div className="stats-card p-4 rounded-lg">
                <div className="text-green-300 text-sm title-font mb-1">Arrivées</div>
                <div className="text-3xl font-bold text-green-400">{resultats.arrivees.length}</div>
              </div>
              <div className="stats-card p-4 rounded-lg">
                <div className="text-red-300 text-sm title-font mb-1">Départs</div>
                <div className="text-3xl font-bold text-red-400">{resultats.departs.length}</div>
              </div>
            </div>

            {/* Détails des mouvements */}
            <div className="grid md:grid-cols-2 gap-6">
              {/* Arrivées */}
              <div className="bg-green-900 bg-opacity-20 rounded-lg p-4 border border-green-500 border-opacity-30">
                <h3 className="title-font text-xl text-green-300 mb-3 flex items-center gap-2">
                  ✅ Arrivées ({resultats.arrivees.length})
                </h3>
                <div className="body-font space-y-1 max-h-60 overflow-y-auto">
                  {resultats.arrivees.length > 0 ? (
                    resultats.arrivees.map((nom, i) => (
                      <div key={i} className="text-gray-200 py-1 px-2 hover:bg-green-800 hover:bg-opacity-30 rounded">
                        {i + 1}. {nom}
                      </div>
                    ))
                  ) : (
                    <p className="text-gray-400 italic">Aucune arrivée</p>
                  )}
                </div>
              </div>

              {/* Départs */}
              <div className="bg-red-900 bg-opacity-20 rounded-lg p-4 border border-red-500 border-opacity-30">
                <h3 className="title-font text-xl text-red-300 mb-3 flex items-center gap-2">
                  ❌ Départs ({resultats.departs.length})
                </h3>
                <div className="body-font space-y-1 max-h-60 overflow-y-auto">
                  {resultats.departs.length > 0 ? (
                    resultats.departs.map((nom, i) => (
                      <div key={i} className="text-gray-200 py-1 px-2 hover:bg-red-800 hover:bg-opacity-30 rounded">
                        {i + 1}. {nom}
                      </div>
                    ))
                  ) : (
                    <p className="text-gray-400 italic">Aucun départ</p>
                  )}
                </div>
              </div>
            </div>

            {/* Groupes */}
            {resultats.groupes.length > 0 && (
              <div className="mt-6 bg-purple-900 bg-opacity-20 rounded-lg p-4 border border-purple-500 border-opacity-30">
                <h3 className="title-font text-xl text-purple-300 mb-3 flex items-center gap-2">
                  🛡️ Groupes et Armées ({resultats.groupes.length})
                </h3>
                <div className="body-font space-y-1 max-h-40 overflow-y-auto">
                  {resultats.groupes.map((groupe, i) => (
                    <div key={i} className="text-gray-200 py-1 px-2 hover:bg-purple-800 hover:bg-opacity-30 rounded">
                      {i + 1}. {groupe}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Visions et événements */}
            {(resultats.visions || resultats.evenements) && (
              <div className="grid md:grid-cols-2 gap-6 mt-6">
                {resultats.visions && (
                  <div className="bg-blue-900 bg-opacity-20 rounded-lg p-4 border border-blue-500 border-opacity-30">
                    <h3 className="title-font text-xl text-blue-300 mb-3">👁️ Visions</h3>
                    <p className="body-font text-gray-200 whitespace-pre-wrap">{resultats.visions}</p>
                  </div>
                )}
                {resultats.evenements && (
                  <div className="bg-yellow-900 bg-opacity-20 rounded-lg p-4 border border-yellow-500 border-opacity-30">
                    <h3 className="title-font text-xl text-yellow-300 mb-3">📰 Événements</h3>
                    <p className="body-font text-gray-200 whitespace-pre-wrap">{resultats.evenements}</p>
                  </div>
                )}
              </div>
            )}

            {/* Rapport du Prévôt */}
            {(resultats.rapportPrevot || resultats.listeVilains.length > 0) && (
              <div className="mt-6 bg-red-900 bg-opacity-20 rounded-lg p-6 border border-red-500 border-opacity-30">
                <h3 className="title-font text-2xl text-red-300 mb-4 flex items-center gap-2">
                  ⚖️ Rapport du Prévôt
                </h3>
                
                {resultats.rapportPrevot && (
                  <div className="mb-4">
                    <h4 className="title-font text-lg text-red-200 mb-2">📋 Synthèse du jour</h4>
                    <div className="bg-black bg-opacity-30 rounded p-3">
                      <p className="body-font text-gray-200 whitespace-pre-wrap">{resultats.rapportPrevot}</p>
                    </div>
                  </div>
                )}

                {resultats.listeVilains.length > 0 && (
                  <div>
                    <h4 className="title-font text-lg text-red-200 mb-2">🚨 Liste des vilains ({resultats.listeVilains.length})</h4>
                    <div className="body-font space-y-1 max-h-40 overflow-y-auto">
                      {resultats.listeVilains.map((vilain, i) => (
                        <div key={i} className="text-gray-200 py-1 px-2 hover:bg-red-800 hover:bg-opacity-30 rounded">
                          {i + 1}. {vilain}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Grande Prévôté - Intercesseurs */}
            {(resultats.vilainsRecherches.length > 0 || resultats.analyseIntercesseur || resultats.vilainsDetectes.length > 0) && (
              <div className="mt-6 bg-purple-950 bg-opacity-30 rounded-lg p-6 border-2 border-purple-500 border-opacity-50">
                <div className="mb-4">
                  <h3 className="title-font text-2xl text-purple-200 mb-1 flex items-center gap-2">
                    ⚜️ Grande Prévôté de France
                  </h3>
                  <p className="body-font text-sm text-purple-300 italic">Intercesseurs Royaux</p>
                </div>
                
                {resultats.vilainsDetectes.length > 0 && (
                  <div className="mb-4 bg-red-900 bg-opacity-40 border-2 border-red-500 rounded-lg p-4">
                    <h4 className="title-font text-xl text-red-300 mb-3 flex items-center gap-2 animate-pulse">
                      🚨 ALERTE - {resultats.vilainsDetectes.length} CORRESPONDANCE(S) DÉTECTÉE(S) !
                    </h4>
                    <div className="space-y-2">
                      {resultats.vilainsDetectes.map((detect, i) => (
                        <div key={i} className="bg-black bg-opacity-50 rounded p-3 border-l-4 border-red-500">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-2xl">{detect.icon}</span>
                            <span className="body-font font-bold text-red-200">{detect.nom}</span>
                          </div>
                          <div className="body-font text-sm text-gray-300 ml-8">
                            <span className="text-yellow-300">{detect.type}</span> - 
                            <span className="italic"> Recherché: {detect.recherche}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {resultats.vilainsRecherches.length > 0 && resultats.vilainsDetectes.length === 0 && (
                  <div className="mb-4 bg-green-900 bg-opacity-20 border border-green-500 rounded-lg p-4">
                    <h4 className="title-font text-lg text-green-300 mb-2">✓ Aucun vilain recherché détecté</h4>
                    <p className="body-font text-sm text-gray-300">
                      Liste de surveillance: {resultats.vilainsRecherches.join(', ')}
                    </p>
                  </div>
                )}

                {resultats.analyseIntercesseur && (
                  <div>
                    <h4 className="title-font text-lg text-purple-200 mb-2">📜 Analyse de l'Intercesseur</h4>
                    <div className="bg-black bg-opacity-30 rounded p-3">
                      <p className="body-font text-gray-200 whitespace-pre-wrap">{resultats.analyseIntercesseur}</p>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Footer */}
      <footer className="text-center mt-12 pt-8 border-t border-purple-500 border-opacity-30 fade-in">
        <p className="body-font text-gray-400">
          Site Unofficiel pour le jeu{' '}
          <a 
            href="https://www.renaissancekingdoms.com/" 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-yellow-300 hover:text-yellow-200 transition-colors"
          >
            Renaissance Kingdoms
          </a>
        </p>
        <p className="body-font text-gray-500 text-sm mt-2">
          Créé par Anthony - © 2025
        </p>
      </footer>
    </div>
  );
};

export default RKDouanes;